import type { ReactNode } from 'react';

import { createElement, Fragment } from 'react';

import MathRenderer from '../components/math-renderer';

// ⚠️ CRITICAL: Math patterns MUST stay synchronized with scripts/content-generation/generate-from-content.js
// Strategy: Match math expressions with symbols, exclude simple currency and invalid expressions
// This ensures ChatGPT-generated math expressions are always rendered with MathJax
const MATH_PATTERNS = [
  // Display math delimiters
  /\$\$[^$]+\$\$/g, // Display math: $$expression$$

  // Inline math expressions (non-greedy to avoid spanning across text)
  /\$(?=.*[a-zA-Z\\^_{}()[\]|><~:=+\-*/])[^$]*?\$/g, // Math expressions (non-greedy to avoid spanning)

  // Math formula tags
  /<math-formula[^>]*>.*?<\/math-formula>/gi, // Math formula tags: <math-formula>content</math-formula>
];

const containsMathFormulaTags = (text: string): boolean => {
  return /<math-formula[^>]*>.*?<\/math-formula>/gi.test(text);
};

// SYNCHRONIZED with generate-from-content.js
export const containsMath = (text: string): boolean => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  if (containsMathFormulaTags(text)) {
    return true;
  }

  // Pre-filter: if text is obviously just currency amounts, skip entirely
  if (/^\$\d+(\.\d{2})?\s+(and|or)\s+\$\d+(\.\d{2})?$/.test(text.trim())) {
    return false; // Simple currency like "$20 and $30"
  }

  // Check for actual math patterns (excluding math-formula tags which are handled above)
  const mathPatterns = MATH_PATTERNS.slice(0, -1); // Exclude the math-formula pattern
  const mathMatches = mathPatterns.some(pattern => {
    pattern.lastIndex = 0; // Reset global regex
    return pattern.test(text);
  });

  if (!mathMatches) {
    return false;
  }

  // Additional check: make sure we're not matching across unrelated currency amounts
  const dollarExpressions =
    text.match(/\$(?=.*[a-zA-Z\\^_{}()[\]|><~:=+\-*/])[^$]*?\$/g) || [];

  // Filter out expressions that span across multiple words (likely invalid)
  const validExpressions = dollarExpressions.filter(expr => {
    const content = expr.slice(1, -1); // Remove $ delimiters

    // If it contains sentence boundaries (periods), it's likely spanning across sentences
    if (/\.\s+[A-Z]/.test(content)) {
      return false;
    }

    // If it contains common English words that indicate natural language, not math
    if (
      /\b(this|that|these|those|means|you|owe|the|bank|have|want|need|get|can|will|would|should|could)\b/i.test(
        content
      )
    ) {
      return false;
    }

    // If it contains "and $" or similar patterns, it's likely spanning across text
    // BUT allow "and" or "or" if they're within LaTeX \text{} commands
    if (/\s+and\s+|\s+or\s+|\s+but\s+|\s+with\s+/.test(content)) {
      // Check if the and/or appears within \text{} commands
      const hasLatexTextCommands = /\\text\{[^}]*(?:and|or)[^}]*\}/.test(
        content
      );
      if (!hasLatexTextCommands) {
        return false; // Reject only if NOT within \text{} commands
      }
    }

    // If it ends with common connecting words followed by space, it's invalid
    if (/\s+(and|or|but|with|for|in|on|at|to|from)\s*$/.test(content)) {
      return false;
    }

    // If it's just numbers and connecting words, it's not math
    if (/^\d+\s+(and|or)\s*$/.test(content)) {
      return false;
    }

    return true;
  });

  if (validExpressions.length === 0) {
    return false;
  }

  return true;
};

// Function to determine if entire text should be treated as math (SYNCHRONIZED with ChatGPT patterns)
const shouldTreatAsMath = (text: string): boolean => {
  // Always treat math-formula tags as math
  if (/<math-formula[^>]*>.*?<\/math-formula>/gi.test(text)) {
    return true;
  }

  // Always treat display math as math
  if (/\$\$[^$]+\$\$/.test(text)) {
    return true;
  }

  // Check if entire text is wrapped in LaTeX delimiters
  const isWrappedInLatex = /^\$[^$]+\$$/;
  if (isWrappedInLatex.test(text.trim())) {
    const content = text.trim().slice(1, -1); // Remove $ delimiters

    // Apply the same filtering logic as containsMath
    // If it contains sentence boundaries (periods), it's likely spanning across sentences
    if (/\.\s+[A-Z]/.test(content)) {
      return false;
    }

    // If it contains common English words that indicate natural language, not math
    if (
      /\b(this|that|these|those|means|you|owe|the|bank|have|want|need|get|can|will|would|should|could)\b/i.test(
        content
      )
    ) {
      return false;
    }

    // If it contains "and $" or similar patterns, it's likely spanning across text
    // BUT allow "and" or "or" if they're within LaTeX \text{} commands
    if (/\s+and\s+|\s+or\s+|\s+but\s+|\s+with\s+/.test(content)) {
      // Check if the and/or appears within \text{} commands
      const hasLatexTextCommands = /\\text\{[^}]*(?:and|or)[^}]*\}/.test(
        content
      );
      if (!hasLatexTextCommands) {
        return false; // Reject only if NOT within \text{} commands
      }
    }

    // If it ends with common connecting words followed by space, it's invalid
    if (/\s+(and|or|but|with|for|in|on|at|to|from)\s*$/.test(content)) {
      return false;
    }

    // If it's just numbers and connecting words, it's not math
    if (/^\d+\s+(and|or)\s*$/.test(content)) {
      return false;
    }

    // DO NOT treat as math if it looks like currency (just numbers, possibly with cents)
    if (/^-?\d+(?:\.\d{2})?$/.test(content)) {
      return false; // Currency like "$20", "$100.50", "-$20"
    }

    // DO NOT treat as math if it's just simple numbers or basic angle measurements
    if (/^[\d°\s,-]+$/.test(content)) {
      return false; // Simple numbers like "30", "60", "90", "30°", "30, 60, 90"
    }

    // DO NOT treat as math if it's just a simple phrase with numbers
    if (/^[\d\s-]+\s+(triangle|degree|degrees|angle|angles)$/i.test(content)) {
      return false; // "30-60-90 triangle", "90 degrees"
    }

    // Everything else in $...$ should be treated as math
    return true;
  }

  return false;
};

// Function to wrap content in math delimiters if not already wrapped
const ensureMathDelimiters = (content: string): string => {
  const trimmed = content.trim();

  // If already wrapped in math delimiters, return as is
  if (trimmed.startsWith('$$') && trimmed.endsWith('$$')) {
    return trimmed;
  }
  if (trimmed.startsWith('$') && trimmed.endsWith('$')) {
    return trimmed;
  }

  // Wrap in inline math delimiters
  return `$${trimmed}$`;
};

// Function to split text into math and non-math segments
export const splitTextForMath = (
  text: string
): Array<{ content: string; isMath: boolean }> => {
  // Pre-filter: if text is obviously just currency amounts, return as single text segment
  if (/^\$\d+(\.\d{2})?\s+(and|or)\s+\$\d+(\.\d{2})?$/.test(text.trim())) {
    return [{ content: text, isMath: false }];
  }

  // If the entire text should be treated as math, return it as a single math segment
  if (shouldTreatAsMath(text)) {
    return [{ content: text, isMath: true }];
  }

  const segments: Array<{ content: string; isMath: boolean }> = [];
  let lastIndex = 0;

  // Find all math expressions
  const mathMatches: Array<{
    start: number;
    end: number;
    match: string;
    isMathFormula: boolean;
  }> = [];

  MATH_PATTERNS.forEach(pattern => {
    let match;
    pattern.lastIndex = 0; // Reset regex
    while ((match = pattern.exec(text)) !== null) {
      // Check if this is a math-formula tag
      const isMathFormula = /<math-formula[^>]*>.*?<\/math-formula>/gi.test(
        match[0]
      );

      mathMatches.push({
        end: match.index + match[0].length,
        isMathFormula,
        match: match[0],
        start: match.index,
      });
    }
  });

  // Sort matches by position
  mathMatches.sort((a, b) => a.start - b.start);

  // Only merge truly overlapping matches, not nearby ones
  const mergedMatches: Array<{
    start: number;
    end: number;
    isMathFormula: boolean;
  }> = [];
  for (const match of mathMatches) {
    const lastMerged = mergedMatches[mergedMatches.length - 1];
    // Only merge if they actually overlap (no gap allowed)
    if (lastMerged && match.start <= lastMerged.end) {
      lastMerged.end = Math.max(lastMerged.end, match.end);
      // If either match is a math-formula, keep that flag
      lastMerged.isMathFormula =
        lastMerged.isMathFormula || match.isMathFormula;
    } else {
      mergedMatches.push({
        end: match.end,
        isMathFormula: match.isMathFormula,
        start: match.start,
      });
    }
  }

  // Create segments
  for (const match of mergedMatches) {
    // Add non-math text before the match
    if (lastIndex < match.start) {
      const nonMathText = text.slice(lastIndex, match.start);
      if (nonMathText.trim()) {
        segments.push({ content: nonMathText, isMath: false });
      }
    }

    // Add the math text
    const mathText = text.slice(match.start, match.end);

    // Math-formula tags are always treated as math
    if (match.isMathFormula) {
      segments.push({ content: mathText, isMath: true });
    } else {
      // For other patterns, double-check it's not actually currency
      const isActuallyMath = shouldTreatAsMath(mathText);

      if (isActuallyMath) {
        segments.push({ content: mathText, isMath: true });
      } else {
        // If it's not actually math (probably currency), treat as regular text
        segments.push({ content: mathText, isMath: false });
      }
    }
    lastIndex = match.end;
  }

  // Add remaining non-math text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText.trim()) {
      segments.push({ content: remainingText, isMath: false });
    }
  }

  // If no math was found, return the entire text as non-math
  if (segments.length === 0) {
    segments.push({ content: text, isMath: false });
  }

  return segments;
};

// Function to parse HTML while preserving math expressions
export const parseHTMLWithMath = (htmlString: string): ReactNode => {
  // Ensure unique keys across the entire parsed tree to avoid duplicate key warnings
  let globalKeyCounter = 0;
  const nextKey = () => `k-${globalKeyCounter++}` as const;
  // Simple HTML parser that handles common tags
  const parseNode = (text: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let currentIndex = 0;

    // First, handle math-formula tags specially
    const mathFormulaRegex = /<math-formula[^>]*>(.*?)<\/math-formula>/gi;
    let mathMatch;

    while ((mathMatch = mathFormulaRegex.exec(text)) !== null) {
      // Add text before the math-formula tag
      if (mathMatch.index > currentIndex) {
        const beforeText = text.slice(currentIndex, mathMatch.index);
        if (beforeText) {
          nodes.push(...parseRegularHTML(beforeText));
        }
      }

      // Process the math-formula content
      const mathContent = mathMatch[1];
      const mathWithDelimiters = ensureMathDelimiters(mathContent);

      // Add the math node
      nodes.push(
        createElement(MathRenderer, {
          children: mathWithDelimiters,
          key: nextKey(),
        })
      );

      currentIndex = mathFormulaRegex.lastIndex;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      if (remainingText) {
        nodes.push(...parseRegularHTML(remainingText));
      }
    }

    return nodes;
  };

  // Helper function to parse regular HTML tags (non-math-formula)
  const parseRegularHTML = (text: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let currentIndex = 0;

    // First handle self-closing br tags
    const brRegex = /<br\s*\/?>/gi;
    const allMatches: Array<{
      end: number;
      fullMatch?: string;
      innerContent?: string;
      start: number;
      tagName?: string;
      type: 'br' | 'tag';
    }> = [];

    // Find all br tags
    let brMatch;
    brRegex.lastIndex = 0;
    while ((brMatch = brRegex.exec(text)) !== null) {
      allMatches.push({
        end: brMatch.index + brMatch[0].length,
        start: brMatch.index,
        type: 'br',
      });
    }

    // Find HTML tags (excluding math-formula which is handled separately)
    const tagRegex = /<([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^>]*)?>([^<]*)<\/\1>/g;
    let match;

    while ((match = tagRegex.exec(text)) !== null) {
      const tagName = match[1];

      // Skip math-formula tags as they're handled separately
      if (tagName.toLowerCase() === 'math-formula') {
        continue;
      }

      allMatches.push({
        end: match.index + match[0].length,
        fullMatch: match[0],
        innerContent: match[2],
        start: match.index,
        tagName: tagName,
        type: 'tag',
      });
    }

    // Sort all matches by position
    allMatches.sort((a, b) => a.start - b.start);

    // Process all matches in order
    for (const matchItem of allMatches) {
      // Add text before the tag
      if (matchItem.start > currentIndex) {
        const beforeText = text.slice(currentIndex, matchItem.start);
        if (beforeText) {
          nodes.push(...processTextSegment(beforeText));
        }
      }

      if (matchItem.type === 'br') {
        // Add br element
        nodes.push(createElement('br', { key: nextKey() }));
      } else if (
        matchItem.type === 'tag' &&
        matchItem.tagName &&
        matchItem.innerContent !== undefined
      ) {
        // Process the content inside the tag
        const processedInner = processTextSegment(matchItem.innerContent);

        // Create the appropriate React element
        if (matchItem.tagName === 'strong') {
          nodes.push(
            createElement('strong', { key: nextKey() }, ...processedInner)
          );
        } else if (matchItem.tagName === 'em') {
          nodes.push(
            createElement('em', { key: nextKey() }, ...processedInner)
          );
        } else if (matchItem.tagName === 'span') {
          nodes.push(
            createElement('span', { key: nextKey() }, ...processedInner)
          );
        } else if (matchItem.tagName === 'a') {
          // Handle anchor tags for internal links using regular anchor tags
          const fullMatch = matchItem.fullMatch || '';
          const hrefMatch = fullMatch.match(/href\s*=\s*["']([^"']+)["']/);
          const href = hrefMatch ? hrefMatch[1] : '#';

          nodes.push(
            createElement(
              'a',
              {
                className:
                  'text-brand-primary hover:text-brand-secondary underline transition-colors duration-200',
                href: href,
                key: nextKey(),
              },
              ...processedInner
            )
          );
        } else {
          // For other tags, just render as span
          nodes.push(
            createElement('span', { key: nextKey() }, ...processedInner)
          );
        }
      }

      currentIndex = matchItem.end;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      if (remainingText) {
        nodes.push(...processTextSegment(remainingText));
      }
    }

    // If no HTML tags were found, just process as text
    if (nodes.length === 0) {
      nodes.push(...processTextSegment(text));
    }

    return nodes;
  };

  const processTextSegment = (text: string): ReactNode[] => {
    if (!containsMath(text)) {
      return [createElement(Fragment, { key: nextKey() }, text)];
    }

    const segments = splitTextForMath(text);
    return segments.map(segment => {
      if (segment.isMath) {
        return createElement(MathRenderer, {
          children: segment.content,
          key: nextKey(),
        });
      }
      return createElement(Fragment, { key: nextKey() }, segment.content);
    });
  };

  const nodes = parseNode(htmlString);
  // Wrap with a keyed Fragment to avoid anonymous fragment warnings
  return createElement(Fragment, { key: nextKey() }, ...nodes);
};

// Function to render text with math expressions (original function)
export const renderTextWithMath = (
  text: string,
  key?: number | string
): ReactNode => {
  // Safety check for undefined/null text
  if (!text || typeof text !== 'string') {
    return '';
  }

  if (!containsMath(text)) {
    return text;
  }

  const segments = splitTextForMath(text);

  return segments.map((segment, index) => {
    const segmentKey = key ? `${key}-${index}` : index;

    if (segment.isMath) {
      // If this is a math-formula tag, extract the content and wrap it properly
      if (/<math-formula[^>]*>.*?<\/math-formula>/gi.test(segment.content)) {
        const mathFormulaMatch = segment.content.match(
          /<math-formula[^>]*>(.*?)<\/math-formula>/i
        );
        if (mathFormulaMatch) {
          const mathContent = mathFormulaMatch[1];
          const mathWithDelimiters = ensureMathDelimiters(mathContent);
          return createElement(MathRenderer, {
            children: mathWithDelimiters,
            key: segmentKey,
          });
        }
      }

      // For regular math expressions, render as is
      return createElement(MathRenderer, {
        children: segment.content,
        key: segmentKey,
      });
    }

    return segment.content;
  });
};

// Enhanced function to render text with both HTML and math
export const renderTextWithMathAndHTML = (
  text: string,
  key?: number | string
): ReactNode => {
  // Safety check for undefined/null text
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Convert newline characters to HTML line breaks
  const textWithLineBreaks = text.replace(/\n/g, '<br />');

  // Check if the text contains HTML tags (including tags with hyphens like math-formula)
  const hasHTMLTags = /<([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^>]*)?>.*?<\/\1>/s;
  const hasBrTags = /<br\s*\/?>/i;

  if (
    hasHTMLTags.test(textWithLineBreaks) ||
    hasBrTags.test(textWithLineBreaks)
  ) {
    // Parse HTML while handling math expressions
    return parseHTMLWithMath(textWithLineBreaks);
  } else {
    // No HTML tags, just handle math
    return renderTextWithMath(textWithLineBreaks, key);
  }
};
